import patientMedicationQuestion, {
  medicationQuestionVersion,
} from "./patientMedicationQuestion";

// CHANGELOG (Reverse chronological order)
// Version 2026100801
// - Search medications locally and confirm optional dose/frequency details with Save and continue.
//
// Version 2026100101
// - Add confirmed patient medication history with name, dose, and frequency.

export default function () {
  return {
    name: "Performance Anxiety Screening",
    description:
      "We can help you find out if medication might help with your performance anxiety",
    version: medicationQuestionVersion,
    service_code: "PERFORMANCE_ANXIETY_FOLLOW_UP",
    live: true,
    staged: true,
    questions: [
      {
        title: "Has the medication been effective for you?",
        description: "",
        type: "radio",
        choices: [
          {
            label: "Yes",
            silent: true,
          },

          {
            label: "No",
            children: [
              {
                title: "Why didn't you find the medication effective?",
                description:
                  "Please tell us in as much detail as you can about your experience with the medication.",
                code: "medication-effective",
                type: "textarea",
              },
            ],
          },
        ],
      },
      {
        title: "Have you experienced any side effects from the medication?",
        description: "",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What side effects did you experience?",
                description:
                  "Please tell us in as much detail as you can about the side effects you experienced with the medication.",
                code: "medication-side-effects",
                type: "textarea",
              },
            ],
          },
          {
            label: "No",
            silent: true,
          },
        ],
      },

      patientMedicationQuestion,
      {
        title:
          "Have you had any surgeries or hospitalizations since your last Kick visit?",
        description:
          "Include any significant emergency room visits, hospital stays, or surgeries, including cosmetic surgeries.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What surgeries or hospitalizations have you had?",
                description:
                  "List all the procedures and events and try to remember when they occurred.",
                code: "hospitalizations",
                type: "textarea",
              },
            ],
          },
          {
            label: "No surgeries or hospitalizations",
            silent: true,
          },
        ],
      },

      {
        title: "Do you have any of the following conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "conditions",
        choices: [
          {
            label:
              "Any problems with poor circulation, or cold or blue extremities?",
          },
          {
            label: "Are you pregnant?",
          },
          {
            label: "Are you breastfeeding?",
          },
          {
            label: "Do you take any medication to lower blood pressure?",
            children: [
              {
                title:
                  "Please list your blood pressure medications and dosages if you know them.",
                description:
                  "list your blood pressure medications and dosages if you know them",
                code: "blood-pressure-medication-details",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any liver problems?",
          },
          {
            label: "Any kidney problems?",
          },
          {
            label: "Hyperthyroidism?",
          },
          {
            label: "Hypotension or low blood pressure?",
          },
          {
            label:
              "Are you currently experiencing any symptoms of sexual dysfunction?",
          },
          {
            label: "Do you have glaucoma?",
          },
          {
            label:
              "Ever been diagnosed with any mental disorders such as bipolar disorder or schizophrenia?",
            children: [
              {
                title: "What mental disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "mental-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Ever been diagnosed with any neurological disorder such as Parkinson’s disease or dementia?",
            children: [
              {
                title:
                  "What neurological disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "neurological-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        title:
          "What is the TOP number of your blood pressure (systolic reading) taken in the last 6 months?",
        description:
          "In order to provide a safe and effective treatment plan, we need an accurate and recent blood pressure measurement. Blood pressure results are a higher number over another lower number; for example: 115/75. Please start by entering the higher number below (you’ll enter the lower number on the next page).",
        code: "systolic",
        type: "bloodpressure-systolic",
        choices: [
          {
            label: "160 or higher",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "140-159",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "120-139",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "95-119",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 95",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },

      {
        title:
          "What is the BOTTOM number of your blood pressure (diastolic reading) taken in the last 6 months?",
        description:
          "Please select the LOWER NUMBER carefully, it is very important to treat you safely.",
        code: "diastolic",
        type: "bloodpressure-diastolic",
        choices: [
          {
            label: "100 or over",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "90-99",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "80-90",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "65-79",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 65",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },

      {
        title: "Have you ever: ",
        type: "checkbox",
        choices: [
          {
            label:
              "Felt you need to cut down on use of a substance (alcohol, cocaine, cannabis, etc)",
          },
          {
            label: "Been annoyed by people criticizing your use of substances?",
          },
          {
            label: "Felt guilty about your use of a substance?",
          },
          {
            label:
              "Needed a drink first thing in the morning as an eye-opener?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        title:
          "Do you have any concerns about your mood, depression, anxiety or worrying?",
        code: "PHQ-GAD-2",
        type: "radio",
        choices: [
          {
            label: "No",
            silent: true,
          },
          {
            label: "Yes",
            children: [
              {
                title:
                  "Over the past two weeks, how often have you felt little interest or pleasure in doing things?",
                code: "PHQ2-1",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt down, depressed or hopeless?",
                type: "radio",
                code: "PHQ2-2",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt nervous, anxious, or on edge?",
                type: "radio",
                code: "GAD2-1",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you not been able to stop or control worrying?",
                code: "GAD2-2",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        title: "Any dosage or medication updates on this follow-up visit?",
        description:
          "We'll pass this information to your doctor to help determine the best course of treatment",

        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What would you like to request?",
                code: "special-requests-radio",
                type: "radio",
                choices: [
                  {
                    label:
                      "I would like to try a higher 20mg dosage of Propranolol",
                  },
                  {
                    label:
                      "I would like to try a cardio-selective beta blocker like Atenolol",
                    children: [
                      {
                        title:
                          "Why you are interested in a cardioselective beta-blocker like Atenolol?",
                        code: "follow-up-why-atenolol",
                        type: "textarea",
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            label: "No, we can move on",
            silent: true,
          },
        ],
      },

      {
        title: "Anything else you would like your doctor to know?",
        description:
          "Before we wrap up, any final things you'd like to tell your doctor? Feel free to ask questions as well.",

        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What would you like to tell your doctor?",
                code: "doctor-let-know",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, we can move on",
            silent: true,
          },
        ],
      },
    ],
  };
}

// -------------------------------------------------------
//
// CHANGELOG
//
// -------------------------------------------------------
// Version 2022012301
// - Change GAD2-2 question to "Over the past two weeks, how often have you not been able to stop or control worrying?"
//
// -------------------------------------------------------
// Version 2023121301
// - Add special requests question for 20mg propranolol and atenolol
//
// -------------------------------------------------------
// Version 2024041201
// - Add follow-up textarea question for blood pressure medication specifics
//
// -------------------------------------------------------
// Version 2025051501
// - Change "Any special requests question" to "any dosage or medication updates" to be more clear
//
// -------------------------------------------------------
// Version 2025101001
// - Add question code 'medications-radio' to medications question for EMR highlighting support
//
// -------------------------------------------------------
// Version 2025110901
// - Add follow-up why atenolol grandchild question for cardioselective beta-blocker like Atenolol
//
// -------------------------------------------------------
//
